import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  categorizeResult,
  type CategorizedCreditEvent,
} from "@/utils/creditHistoryCategorize";

const ENDPOINT = "https://api.snapshot.systems/device-credits";

interface DeviceCreditHistoryBucket {
  id: string; // "YYYY-MM"
  data: Record<string, string>;
}

export interface DeviceCreditHistoryResult {
  events: CategorizedCreditEvent[];
  months: string[]; // sorted desc
  raw: DeviceCreditHistoryBucket[];
}

export const fetchDeviceCreditHistory = async (
  deviceId: string,
): Promise<DeviceCreditHistoryResult> => {
  const { data: { session } } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }

  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({ deviceID: deviceId }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `device-credits API ${response.status}: ${text.slice(0, 200)}`,
    );
  }

  const json = (await response.json()) as DeviceCreditHistoryBucket[];
  const buckets = Array.isArray(json) ? json : [];

  const events: CategorizedCreditEvent[] = [];
  for (const bucket of buckets) {
    if (!bucket?.data) continue;
    for (const [ts, result] of Object.entries(bucket.data)) {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) continue;
      events.push({
        timestamp: d,
        month: bucket.id,
        result: String(result),
        category: categorizeResult(String(result)),
      });
    }
  }

  events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const months = buckets
    .map((b) => b.id)
    .filter(Boolean)
    .sort((a, b) => b.localeCompare(a));

  return { events, months, raw: buckets };
};

export const useDeviceCreditHistory = (
  deviceId: string | null | undefined,
  enabled: boolean = true
) => {
  return useQuery<DeviceCreditHistoryResult>({
    queryKey: ["device-credit-history", deviceId],
    enabled: !!deviceId && enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchDeviceCreditHistory(deviceId as string),
  });
};
